import React, { useState, useEffect } from 'react';
import { Card, Table, Button, DatePicker, Select, Input, Tag, message, Tooltip, Space } from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import supplyChainService, { InboundShipment } from '../../../services/supplyChainService';
import { ShipmentForm } from './components/ShipmentForm';

const { RangePicker } = DatePicker;
const { Option } = Select;

const ShipmentTracker: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [shipments, setShipments] = useState<InboundShipment[]>([]);
    const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
    const [searchText, setSearchText] = useState('');

    // Modal State
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingShipment, setEditingShipment] = useState<InboundShipment | undefined>(undefined);

    useEffect(() => {
        fetchShipments();
    }, [filterStatus]);

    const fetchShipments = async () => {
        setLoading(true);
        try {
            const params: any = {};
            if (filterStatus) params.status = filterStatus;
            const res = await supplyChainService.getInboundShipments(params);
            setShipments(res.data);
        } catch (error) {
            message.error('加载货件失败');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingShipment(undefined);
        setIsModalVisible(true);
    };

    const handleEdit = (record: InboundShipment) => {
        setEditingShipment(record);
        setIsModalVisible(true);
    };

    const handleModalSuccess = () => {
        setIsModalVisible(false);
        fetchShipments();
    };

    // 计算在途天数
    const calculateTransitDays = (record: InboundShipment) => {
        if (!record.shipment_date) return '-';
        const start = dayjs(record.shipment_date);
        const end = record.shelf_date ? dayjs(record.shelf_date) : dayjs(); // 如果已上架用上架时间，否则用当前时间
        return end.diff(start, 'day');
    };

    const columns = [
        {
            title: '货件信息',
            fixed: 'left' as 'left',
            width: 200,
            render: (_: any, record: InboundShipment) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>{record.shipment_id}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>{record.shipment_name}</div>
                    <Tag color="blue">{record.shop_name}</Tag>
                </div>
            )
        },
        {
            title: '物流渠道',
            width: 150,
            render: (_: any, record: InboundShipment) => (
                <div>
                    <div>{record.logistics_provider_name || '-'}</div>
                    <Tag>{record.logistics_channel || '-'}</Tag>
                    <div style={{ fontSize: '12px' }}>{record.carrier_tracking_number}</div>
                </div>
            )
        },
        {
            title: '状态',
            dataIndex: 'shipment_status',
            width: 100,
            render: (status: string) => {
                const colorMap: any = {
                    'WORKING': 'default',
                    'SHIPPED': 'orange',
                    'RECEIVING': 'processing',
                    'CLOSED': 'success',
                    'CANCELLED': 'error',
                    'DELETED': 'error'
                };
                return <Tag color={colorMap[status]}>{status}</Tag>;
            }
        },
        // 时间轴列
        { title: '发货日期', dataIndex: 'shipment_date', width: 120, render: (t: string) => t || '-' },
        { title: '预计到货', dataIndex: 'estimated_arrival_date', width: 120, render: (t: string) => t || '-' },
        { title: '实际签收', dataIndex: 'actual_arrival_date', width: 120, render: (t: string) => t || '-' },
        { title: '上架时间', dataIndex: 'shelf_date', width: 120, render: (t: string) => t || '-' },
        // 计算列
        {
            title: '在途天数',
            key: 'transit_days',
            width: 100,
            render: (_: any, record: InboundShipment) => {
                const days = calculateTransitDays(record);
                let color = 'green';
                if (typeof days === 'number') {
                    if (days > 45) color = 'red';
                    else if (days > 30) color = 'orange';
                }
                return <Tag color={color}>{days} 天</Tag>;
            }
        },
        // 数量与差异
        {
            title: '数量(发/收)',
            width: 120,
            render: (_: any, record: InboundShipment) => {
                // 简单汇总 items (需后端支持聚合，这里暂时mock或需前端计算)
                // 假设 InboundShipment API 返回了 total_shipped/received
                return <span>Wait Backend</span>;
            }
        },
        {
            title: '总实重(kg)', dataIndex: 'total_weight_kg', width: 100
        },
        {
            title: '总体积(cbm)', dataIndex: 'total_volume_cbm', width: 100
        },
        {
            title: '预估运费', dataIndex: 'shipping_price', width: 100,
            render: (val: number) => `¥${val}`
        },
        {
            title: '仓库', dataIndex: 'destination_fulfillment_center_id', width: 80
        }
    ];

    return (
        <Card title="全链路货件追踪" extra={
            <Space>
                <Input placeholder="搜索货件号/物流单号" prefix={<SearchOutlined />} style={{ width: 200 }} onChange={e => setSearchText(e.target.value)} />
                <Select placeholder="状态筛选" style={{ width: 120 }} allowClear onChange={setFilterStatus}>
                    <Option value="WORKING">建立中</Option>
                    <Option value="SHIPPED">已发货</Option>
                    <Option value="RECEIVING">接收中</Option>
                    <Option value="CLOSED">已关闭</Option>
                </Select>
                <Button icon={<ReloadOutlined />} onClick={fetchShipments} />
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>创建货件</Button>
            </Space>
        }>
            <Table
                dataSource={shipments.filter(s => s.shipment_id.includes(searchText) || (s.carrier_tracking_number && s.carrier_tracking_number.includes(searchText)))}
                columns={[
                    ...columns,
                    {
                        title: '操作',
                        key: 'action',
                        width: 80,
                        fixed: 'right',
                        render: (_, record) => (
                            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                        )
                    }
                ]}
                rowKey="id"
                scroll={{ x: 1500 }}
                loading={loading}
                pagination={{ pageSize: 20 }}
            />

            <ShipmentForm
                visible={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onSuccess={handleModalSuccess}
                initialValues={editingShipment}
            />
        </Card>
    );
};

export default ShipmentTracker;
